import { z } from 'zod';
import type { TenantLifecycleStatus } from './saas';
import { email } from './common';

/** Tenant slug (subdomain / company code). Optional at sign-in: the server finds the tenant from the email. */
export const tenantSlug = z
  .string()
  .trim()
  .toLowerCase()
  .regex(/^[a-z0-9][a-z0-9-]{0,62}$/, 'Enter your company code');

export const loginInput = z.object({
  email,
  password: z.string().min(1, 'Required').max(500),
  tenant: tenantSlug.optional(),
});

export const mfaInput = z.object({ code: z.string().trim().regex(/^\d{6}$/, 'Enter the 6-digit code') });

/** Second sign-in step: an authenticator code or one of the one-time recovery codes. */
/** Turning two-factor off needs the password and a current code (or a recovery code, for a lost phone). */
export const mfaDisableInput = z
  .object({
    password: z.string().min(1, 'Required').max(500),
    code: z.string().trim().regex(/^\d{6}$/, 'Enter the 6-digit code').optional(),
    recoveryCode: z.string().trim().min(8, 'Enter a recovery code').max(40).optional(),
  })
  .refine((v) => Boolean(v.code) !== Boolean(v.recoveryCode), { message: 'Enter the 6-digit code from your app, or a recovery code', path: ['code'] });
export type MfaDisableInput = z.infer<typeof mfaDisableInput>;

export const mfaVerifyInput = z
  .object({
    code: z.string().trim().regex(/^\d{6}$/, 'Enter the 6-digit code').optional(),
    recoveryCode: z.string().trim().min(8, 'Enter a recovery code').max(40).optional(),
  })
  .refine((v) => Boolean(v.code) !== Boolean(v.recoveryCode), { path: ['code'], message: 'Enter the 6-digit code' });

/** Re-authentication before a sensitive action: the password or an authenticator code. */
export const stepUpInput = z
  .object({
    password: z.string().min(1).max(500).optional(),
    code: z.string().trim().regex(/^\d{6}$/, 'Enter the 6-digit code').optional(),
  })
  .refine((v) => Boolean(v.password) || Boolean(v.code), { path: ['password'], message: 'Required' });

/** NIST 800-63B: length over composition rules; block the obvious. */
export const newPassword = z
  .string()
  .min(12, 'Use at least 12 characters')
  .max(200)
  .refine((v) => !/^(.)\1+$/.test(v), 'Too predictable');

export const changePasswordInput = z.object({ currentPassword: z.string().min(1), newPassword });
export const forgotPasswordInput = z.object({ email, tenant: tenantSlug.optional() });
export const resetPasswordInput = z.object({ token: z.string().min(20).max(200), newPassword });

export interface SessionUser {
  id: string;
  name: string;
  email: string;
  kind: 'staff' | 'portal';
  roleName: string;
  permissions: string[];
  organizationId: string | null;
  employeeId: string | null;
  mfaEnabled: boolean;
  /** K-2: Denwen MSP holds proof of this address (User.emailVerifiedAt); the account page offers to verify it. */
  emailVerified?: boolean;
  /** How this session signed in. */
  authMethod: 'password' | 'sso' | 'passkey';
  /** K3: passkeys registered on this account (a sign-in method and a second factor). */
  passkeyCount?: number;
  tenant: {
    id: string;
    name: string;
    slug: string;
    currency: string;
    timezone: string;
    // Phase I (I-A): the SaaS lifecycle, for the trial and restricted-account banners.
    status?: TenantLifecycleStatus;
    trialEndsAt?: string | null;
  };
  /** Phase I (I-D): set while Denwen MSP support (a platform admin) is acting as this user. */
  support?: SupportBanner | null;
  /** Phase I (I-D): the user's platform console role. `mfaOk` is false until they use MFA. */
  platform?: { role: PlatformRole; mfaOk: boolean } | null;
}

export type PlatformRole = 'owner' | 'support' | 'read_only';

export interface SupportBanner {
  adminName: string;
  breakGlass: boolean;
  expiresAt: string;
}

export interface TenantChoice {
  slug: string;
  name: string;
}

export type MfaMethod = 'totp' | 'recovery' | 'passkey';

export type LoginResult =
  | { status: 'ok'; user: SessionUser }
  /**
   * A second factor is needed. `methods` lists what this account can use; when the tenant requires
   * phishing-resistant MFA for this account (K3), it is only "passkey".
   */
  | { status: 'mfa_required'; methods?: MfaMethod[] }
  /** K3: the tenant requires a passkey for this account and it has none yet: show passkey enrolment. */
  | { status: 'passkey_enrollment_required' }
  /** The tenant requires MFA and this user has not enrolled yet: show the enrolment step. */
  | { status: 'mfa_enrollment_required' }
  /** The email and password match accounts in several tenants: ask which company. */
  | { status: 'tenant_required'; tenants: TenantChoice[] };
